import React from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  Paper,
  Chip,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { GRADIENTS } from '../../../styles/theme';
import RoiCanvas, { RoiCanvasRef } from '../../../components/RoiCanvas';
import { EditMode } from '../types';

interface RoiWorkCanvasProps {
  showDualView: boolean;
  isDark: boolean;
  // Reference panel
  referenceContainerRef: React.RefObject<HTMLDivElement>;
  originalImageUrl: string;
  originalCctvDataMap: Map<string, any>;
  selectedCctv: string;
  selectedRoiId: string;
  setSelectedRoiId: (id: string) => void;
  setRightDrawerOpen: (open: boolean) => void;
  unifiedSizes: {
    reference: { width: string | number; height: string | number };
    workspace: { width: string | number; height: string | number };
  };
  // Workspace panel
  workspaceContainerRef: React.RefObject<HTMLDivElement>;
  cctvImageUrl: string;
  roiData: any;
  roiCanvasRef: React.RefObject<RoiCanvasRef>;
  roiEditMode: EditMode;
  setRoiEditMode: (mode: EditMode) => void;
  editingRoiId: string;
  setEditingRoiId: (id: string) => void;
  editingOriginalRoi: number[] | null;
  setEditingOriginalRoi: (roi: number[] | null) => void;
  isSelectingRoiForEdit: boolean;
  setIsSelectingRoiForEdit: (v: boolean) => void;
  currentDrawingPoints: number[];
  setCurrentDrawingPoints: (points: number[]) => void;
  hasUnsavedChanges: boolean;
  setError: (msg: string) => void;
  // Handlers
  handleCreateRoi: (points: number[]) => void;
  handleUpdateRoi: (id: string, points: number[]) => void;
  handleConfirmDelete: (id: string) => void;
  handleSave: () => void;
  handleFinalSave: () => void;
  handleRollback: () => void;
  setRoiData: (data: any) => void;
  selectedRoiFile: string;
}

export const RoiWorkCanvasArea: React.FC<RoiWorkCanvasProps> = ({
  showDualView,
  isDark,
  referenceContainerRef,
  originalImageUrl,
  originalCctvDataMap,
  selectedCctv,
  selectedRoiId,
  setSelectedRoiId,
  setRightDrawerOpen,
  unifiedSizes,
  workspaceContainerRef,
  cctvImageUrl,
  roiData,
  roiCanvasRef,
  roiEditMode,
  setRoiEditMode,
  editingRoiId,
  setEditingRoiId,
  editingOriginalRoi,
  setEditingOriginalRoi,
  isSelectingRoiForEdit,
  setIsSelectingRoiForEdit,
  currentDrawingPoints,
  setCurrentDrawingPoints,
  hasUnsavedChanges,
  setError,
  handleCreateRoi,
  handleUpdateRoi,
  handleConfirmDelete,
  handleSave,
  handleFinalSave,
  handleRollback,
  setRoiData,
  selectedRoiFile,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{
      flex: 1,
      display: 'flex',
      bgcolor: isDark ? '#0a0a0a' : '#f5f5f5',
      position: 'relative',
      overflow: 'hidden',
      alignItems: 'flex-start'
    }}>

      {showDualView && (
        <Box sx={{
          flex: 1,
          height: '100%',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Paper
            elevation={0}
            sx={{
              px: 2,
              height: 40,
              borderBottom: '1px solid',
              borderColor: 'divider',
              borderRadius: 0,
              bgcolor: isDark ? alpha(theme.palette.background.paper, 0.5) : theme.palette.background.paper,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Typography variant="caption" sx={{
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: 1
            }}>
              REFERENCE
            </Typography>
          </Paper>
          <Box ref={referenceContainerRef} sx={{
            flex: 1,
            position: 'relative',
            bgcolor: isDark ? '#000' : '#fafafa',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: 2
          }}>
            {originalImageUrl ? (
              <Box sx={{
                width: unifiedSizes.reference.width,
                height: unifiedSizes.reference.height,
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0
              }}>
                <RoiCanvas
                  imageSrc={originalImageUrl}
                  rois={originalCctvDataMap.get(selectedCctv)?.rois || {}}
                  editable={false}
                  onRoiClick={(id) => { setSelectedRoiId(id); setRightDrawerOpen(true); }}
                  selectedRoiId={selectedRoiId}
                  isMobile={false}
                  fullscreen={false}
                />
              </Box>
            ) : (
              <Box sx={{
                mt: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Typography color="text.disabled">No Image</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Paper
          elevation={0}
          sx={{
            px: 2,
            height: 40,
            borderBottom: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.primary.main, 0.02),
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography variant="caption" sx={{
            color: 'primary.main',
            fontWeight: 600,
            letterSpacing: 1
          }}>
            WORKSPACE
          </Typography>
          {hasUnsavedChanges && (
            <Chip
              label="Editing"
              size="small"
              className="animate-pulse-glow"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: alpha(theme.palette.warning.main, 0.2),
                color: 'warning.main',
                border: '1px solid',
                borderColor: alpha(theme.palette.warning.main, 0.3)
              }}
            />
          )}
        </Paper>
        <Box ref={workspaceContainerRef} sx={{
          flex: 1,
          position: 'relative',
          bgcolor: isDark ? '#000' : '#fafafa',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          pt: 2
        }}>
          {cctvImageUrl ? (
            <>
              <Box sx={{
                width: unifiedSizes.workspace.width,
                height: unifiedSizes.workspace.height,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                position: 'relative',
                flexShrink: 0
              }}>
                <RoiCanvas
                  ref={roiCanvasRef}
                  imageSrc={cctvImageUrl}
                  rois={editingRoiId && roiEditMode === 'update'
                    ? Object.fromEntries(Object.entries(roiData?.rois || {}).filter(([id]) => id !== editingRoiId))
                    : roiData?.rois || {}
                  }
                  editable={true}
                  onRoiClick={(id) => {
                    if (isSelectingRoiForEdit) {
                      setSelectedRoiId(id);
                      setEditingRoiId(id);
                      setEditingOriginalRoi([...roiData.rois[id]]);
                      setRoiEditMode('update');
                      setIsSelectingRoiForEdit(false);
                    } else {
                      setSelectedRoiId(id);
                      setRightDrawerOpen(true);
                    }
                  }}
                  selectedRoiId={selectedRoiId}
                  editMode={roiEditMode}
                  onRoiCreate={handleCreateRoi}
                  onRoiUpdate={handleUpdateRoi}
                  onPointsChange={setCurrentDrawingPoints}
                  isMobile={false}
                  fullscreen={false}
                  isSelectingForEdit={isSelectingRoiForEdit}
                />
              </Box>

              {/* Workspace Control Bar */}
              <Paper
                elevation={2}
                sx={{
                  width: '100%',
                  maxWidth: 'calc(100% - 32px)',
                  mx: 2,
                  mt: 2,
                  px: 2,
                  py: 1.5,
                  bgcolor: isDark ? alpha(theme.palette.background.paper, 0.95) : 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  zIndex: 10,
                  flexWrap: 'wrap',
                  flexShrink: 0
                }}
              >
                {/* Left: ROI Actions */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {roiEditMode === 'create' || roiEditMode === 'update' || roiEditMode === 'delete' ? (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          if (roiEditMode === 'delete' && editingRoiId) {
                            handleConfirmDelete(editingRoiId);
                          } else {
                            roiCanvasRef.current?.completeRoi();
                          }
                        }}
                        disabled={roiEditMode === 'delete' ? false : currentDrawingPoints.length < 6}
                        sx={{
                          bgcolor: roiEditMode === 'delete' ? theme.palette.error.main : theme.palette.success.main,
                          color: '#fff',
                          '&:hover': {
                            bgcolor: roiEditMode === 'delete' ? theme.palette.error.dark : theme.palette.success.dark,
                            boxShadow: `0 2px 8px ${alpha(roiEditMode === 'delete' ? theme.palette.error.main : theme.palette.success.main, 0.3)}`
                          },
                          '&.Mui-disabled': {
                            bgcolor: alpha(theme.palette.action.disabled, 0.5),
                            color: theme.palette.action.disabled
                          }
                        }}
                      >
                        {roiEditMode === 'delete' ? '삭제 확인' : '완료'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          roiCanvasRef.current?.cancelRoiEdit();
                          if (roiEditMode === 'update' && editingOriginalRoi && editingRoiId) {
                            const newRois = { ...roiData!.rois, [editingRoiId]: editingOriginalRoi };
                            setRoiData({ ...roiData!, rois: newRois });
                            setEditingRoiId('');
                            setEditingOriginalRoi(null);
                          }
                          setRoiEditMode(null);
                          setSelectedRoiId('');
                          setEditingRoiId('');
                          setCurrentDrawingPoints([]);
                        }}
                        sx={{
                          borderColor: alpha(theme.palette.error.main, 0.5),
                          color: 'error.main',
                          '&:hover': {
                            borderColor: 'error.main',
                            bgcolor: alpha(theme.palette.error.main, 0.1)
                          }
                        }}
                      >
                        취소
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => { setRoiEditMode('create'); setSelectedRoiId(''); setCurrentDrawingPoints([]); }}
                      sx={{
                        bgcolor: theme.palette.success.main,
                        color: '#fff',
                        '&:hover': {
                          bgcolor: theme.palette.success.dark,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`
                        }
                      }}
                    >
                      추가
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      if (selectedRoiId && roiData?.rois[selectedRoiId]) {
                        setEditingRoiId(selectedRoiId);
                        setEditingOriginalRoi([...roiData.rois[selectedRoiId]]);
                        setRoiEditMode('update');
                        setIsSelectingRoiForEdit(false);
                      } else {
                        setIsSelectingRoiForEdit(true);
                        setError('수정할 ROI를 클릭하세요');
                        setTimeout(() => setError(''), 3000);
                      }
                    }}
                    disabled={!selectedRoiId || roiEditMode !== null || !selectedCctv || !roiData}
                    sx={{
                      bgcolor: isSelectingRoiForEdit ? theme.palette.warning.main : theme.palette.primary.main,
                      color: '#fff',
                      '&:hover': {
                        bgcolor: isSelectingRoiForEdit ? theme.palette.warning.dark : theme.palette.primary.dark,
                        boxShadow: `0 2px 8px ${alpha(isSelectingRoiForEdit ? theme.palette.warning.main : theme.palette.primary.main, 0.3)}`
                      },
                      '&.Mui-disabled': {
                        bgcolor: alpha(theme.palette.action.disabled, 0.5),
                        color: theme.palette.action.disabled
                      }
                    }}
                  >
                    {isSelectingRoiForEdit ? 'ROI 선택 중...' : '수정'}
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setRoiEditMode('delete');
                      setEditingRoiId(selectedRoiId);
                    }}
                    disabled={!selectedRoiId || roiEditMode !== null}
                    sx={{
                      bgcolor: theme.palette.error.main,
                      color: '#fff',
                      '&:hover': {
                        bgcolor: theme.palette.error.dark,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`
                      },
                      '&.Mui-disabled': {
                        bgcolor: alpha(theme.palette.action.disabled, 0.5),
                        color: theme.palette.action.disabled
                      }
                    }}
                  >
                    삭제
                  </Button>
                </Box>

                {/* Right: Save/Rollback */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {hasUnsavedChanges && (
                    <Chip
                      label="변경사항 있음"
                      size="small"
                      color="warning"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}

                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleRollback}
                    disabled={!hasUnsavedChanges}
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                      color: 'warning.main',
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.warning.main, 0.25),
                        borderColor: theme.palette.warning.main,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.2)}`
                      },
                      '&.Mui-disabled': {
                        bgcolor: alpha(theme.palette.action.disabled, 0.5),
                        color: theme.palette.action.disabled,
                        border: `1px solid ${alpha(theme.palette.action.disabled, 0.3)}`
                      }
                    }}
                  >
                    롤백
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges}
                    sx={{
                      background: GRADIENTS.primary,
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5
                      }
                    }}
                  >
                    저장
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleFinalSave}
                    disabled={!selectedRoiFile}
                    sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                      fontWeight: 700,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5
                      }
                    }}
                  >
                    최종 저장
                  </Button>
                </Box>
              </Paper>
            </>
          ) : (
            <Box sx={{ mt: 10 }}><Typography color="text.disabled">CCTV를 선택하세요</Typography></Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
